<script setup lang="ts">
import { VFileInput } from 'vuetify/components';
import { useFieldTexts } from '@/composables';
import {
    useAttrs, computed, ref, onMounted,
} from 'vue';
import { requiredValidator } from '@/utils/validationUtils';

type PropsType = {
    modelValue?: File | null;

    /** With `multiple`: the picked files (`v-model:files`); `modelValue` then stays unused. */
    files?: File[];
    multiple?: boolean;
    label?: string;
    placeholder?: string;
    variant?: VFileInput['$props']['variant'];
    density?: VFileInput['$props']['density'];
    color?: string;
    clearable?: boolean;
    loading?: boolean;
    isLabelTranslated?: boolean;
    accept?: string;
    showSize?: boolean;
    prependIcon?: string;
    hint?: string;
    persistentHint?: boolean;
    name?: string;
    dataType?: string;
};

const props = withDefaults(defineProps<PropsType>(), {
    variant: 'outlined',
    density: 'compact',
    clearable: true,
    loading: false,
    isLabelTranslated: true,
    showSize: true,
    multiple: false,
    prependIcon: 'mdi-paperclip',
    dataType: 'file',
});

const emit = defineEmits<{
    'update:modelValue': [value: File | null];
    'update:files': [files: File[]];
    'click:clear': [];
    'click:append': [];
    'click:prepend': [];
    'click:appendInner': [];
    'click:prependInner': [];
}>();

defineOptions({ inheritAttrs: false });

const attrs = useAttrs();

const { getLabel, getPlaceholder } = useFieldTexts({
    label: props.label,
    placeholder: props.placeholder,
    isTranslated: props.isLabelTranslated,
});

const allRules = computed(() => {
    const rules: Array<(v: unknown) => boolean | string> = [];

    if (attrs.required) {
        rules.push(requiredValidator);
    }

    return rules;
});

const handleUpdate = (value: File | File[] | null) => {
    if (props.multiple) {
        emit('update:files', Array.isArray(value) ? value : value ? [value] : []);

        return;
    }

    const fileValue = Array.isArray(value) ? value[0] || null : value;
    emit('update:modelValue', fileValue);
};

const handleClear = () => {
    emit('click:clear');
};

const fileInputRef = ref<VFileInput | null>(null);

onMounted(() => {
    const input = fileInputRef.value?.$el?.querySelector('input[type="file"]');
    if (input) {
        input.dataset.type = props.dataType;
    }
});
</script>

<template>
    <VFileInput
        ref="fileInputRef"
        :model-value="multiple ? (files ?? []) : modelValue"
        :multiple="multiple"
        :label="getLabel"
        :placeholder="getPlaceholder"
        :variant="variant"
        :density="density"
        :color="color"
        :clearable="clearable"
        :loading="loading"
        :rules="allRules"
        :accept="accept"
        :show-size="showSize"
        :prepend-icon="prependIcon"
        :hint="hint"
        :persistent-hint="persistentHint"
        :name="name"
        class="b-file-input"
        v-bind="attrs"
        @update:model-value="handleUpdate"
        @click:clear="handleClear"
        @click:append="emit('click:append')"
        @click:prepend="emit('click:prepend')"
        @click:append-inner="emit('click:appendInner')"
        @click:prepend-inner="emit('click:prependInner')"
    >
        <template
            v-if="$slots.prepend"
            #prepend
        >
            <slot name="prepend" />
        </template>
        <template
            v-if="$slots.append"
            #append
        >
            <slot name="append" />
        </template>
        <template
            v-if="$slots['prepend-inner']"
            #prepend-inner
        >
            <slot name="prepend-inner" />
        </template>
        <template
            v-if="$slots['append-inner']"
            #append-inner
        >
            <slot name="append-inner" />
        </template>
    </VFileInput>
</template>

<style scoped>
.b-file-input :deep(.v-field__input) {
    white-space: nowrap;
    overflow-x: auto;
}

.b-file-input :deep(.v-file-input__text) {
    white-space: nowrap;
    overflow-x: auto;
}
</style>
